// The map's brightness dimmer — the bridge control we cannot put on the monitor.
//
// An ECDIS has a hardware brightness control and its night colour table is
// calibrated against a known display luminance. We have neither: we are a web
// console on whatever laptop is on the chart table, and we cannot touch its
// backlight. So the console carries its own.
//
// WHY IT IS NEEDED AT ALL, given a Night theme already exists. The Night table
// re-casts the CHROME — surfaces, ink, hairlines — and main.ts dims the raster
// BASEMAP to 0.24 brightness. Nothing dims the field raster: windfield.ts paints
// a full-bleed banded surface at alpha 200 and applyLeadStyle pins its
// raster-opacity to 1 on every hour step. The field is the largest lit area on
// the screen and the only one that never got a night calibration, which is
// exactly the client's complaint. The governing quantity for dark adaptation is
// TOTAL EMITTED LIGHT — lit area x luminance — so a large mid-luminance surface
// costs more adaptation than a small bright one, and desaturating it barely
// moves the number.
//
// WHY A USER CONTROL rather than a fixed night value. Bridges differ, monitors
// differ, and the person whose night vision is at stake is the one looking at
// it. The range therefore runs all the way to black on purpose: someone who does
// not care leaves it up, someone it bothers can take it to almost nothing. We do
// not get to decide where "sensible" stops.
//
// WHAT DIMS. The filter lands on MapLibre's CANVAS CONTAINER, so everything that
// is map INFORMATION goes down together — field raster, direction arrows, vector
// overlays, buoy and vessel symbols, and the DOM markers, which MapLibre parents
// to that same element. It deliberately does NOT land on `.map`, because the
// layer control and the dimmer's own control live there: a dimmer that dims the
// widget you need to undo it with is a trap, and at the bottom of the range it
// would be an unrecoverable one.

import { getPref, setPref } from "../ui/prefs.js";

/** The themes that get a dimmer. Day is fighting ambient light on a sunlit
 *  bridge and wants every candela it has — a dimmer there is a way to make the
 *  map unreadable, not a comfort. */
export type DimTheme = "dusk" | "night";

const PREF_KEY: Record<DimTheme, string> = {
  dusk: "map_dim_dusk",
  night: "map_dim_night",
};

/**
 * Slider position → the multiplier handed to CSS `brightness()`.
 *
 * THE CURVE, and why it is not the identity. Two power laws stack here:
 *
 *   1. CSS filter functions operate on sRGB-ENCODED values, so `brightness(b)`
 *      multiplies the encoded channel, not the light. Luminance therefore goes
 *      as a power of b — textbook 2.2, but MEASURED at n = 1.96 over the
 *      console's own Night map in dev/nightfield.ts. The measured value is lower
 *      than the textbook one, and for a reason worth keeping: a night map's
 *      pixels are dark, and dark sRGB values sit in the linear toe of the
 *      transfer function rather than in its 2.4-power leg. The fit drifts from
 *      2.21 at the top of the range to 1.67 at the bottom; 1.96 is the average
 *      across the working range.
 *   2. Perceived lightness goes as roughly the cube root of luminance (CIE L*).
 *
 * Composing them, perceived brightness ~ b^(1.96/3) = b^0.65. A slider wired
 * straight to `b` therefore spends most of its travel in a range that already
 * looks bright, and crosses the entire useful dark region in its last few
 * percent — the opposite of what someone whose eyes are adapting needs.
 *
 * Inverting that exponent gives a slider whose PERCEPTION is linear in its
 * position: b = pos^(3/1.96) = pos^1.53, rounded to 1.5 because the third digit
 * of a fitted exponent is not a real number and pos^1.5 is pos*sqrt(pos).
 * Checked back against the measurements, perceived brightness tracks slider
 * position within about 0.02 from full down to pos 0.3, going mildly compressive
 * below that. Half travel is half perceived brightness and about a sixth of the
 * luminance; the bottom quarter of the slider — where a dark-adapted watchkeeper
 * actually works — gets a quarter of the travel instead of a rounding error.
 *
 * pos 0 yields 0: genuinely black, by request. The map disappearing entirely is
 * a legitimate thing to ask a dimmer for, and it stays recoverable because the
 * control itself is outside the dimmed element.
 */
export const DIM_GAMMA = 1.5;

export function dimToBrightness(pos: number): number {
  const p = Math.max(0, Math.min(1, pos));
  return p ** DIM_GAMMA;
}

/**
 * Where each dark theme starts. Derived, not chosen.
 *
 * IHO S-52 Edition 6.1.1 Table 2 states its three colour tables as ABSOLUTE
 * luminances in cd/m2, and it caps AREA FILLS far harder than anything else:
 *
 *              brightest white     max area fill
 *   Day             80                  65
 *   Dusk            36                  12
 *   Night            5.0                 1.3
 *
 * §2.1.1 says why in one sentence: at night "so little luminance is tolerated
 * that area colours are reduced to shades of dark grey... and only fine lines
 * can be bright". Our field raster is an area fill — a full-bleed one — so the
 * area-fill row is the row that governs it, and the ratios are the transferable
 * part (we cannot know the bridge laptop's absolute cd/m2 from a browser).
 *
 * Measured in dev/nightfield.ts over the console's own map, the Day map's mean
 * relative luminance is 0.501 and the undimmed Night map's is 0.093 — a ratio of
 * 5.4x where S-52's area-fill ratio (65/1.3) is 50x. That gap, about 9x, IS the
 * client's complaint expressed as a number.
 *
 * Solving each theme for the S-52 ratio, through the measured curve:
 *   Night  target 0.501/50 = 0.0100  ->  needs 10.7% of undimmed  ->  pos 0.47
 *   Dusk   target 0.501 x 12/65 = 0.0924  ->  50.6% of undimmed  ->  pos 0.79
 *
 * Rounded to 0.50 and 0.80: both round slightly BRIGHTER than the derivation,
 * which is the safe direction for a console whose job is to be read, and both
 * land on a slider position a person can recognise and return to.
 *
 * Two honesty notes that belong with the numbers. S-52 §5.2.3.1 restricts its
 * own colour tolerances to the bright-sun table, because the CIE perception
 * model "has not yet been evaluated at the low luminances of the night colour
 * table, at which the less colour-sensitive rods of the eye take over" — the
 * standard does not claim its own metric holds at night, so neither do we. And
 * this is a STARTING POINT that persists the moment it is moved: IEC 62288
 * §7.2.1.1 asks only that the range be "sufficient to maintain the user's dark
 * adaptation at night", which is a statement about reach, not about a default.
 */
export const DIM_DEFAULTS: Record<DimTheme, number> = {
  dusk: 0.8,
  night: 0.5,
};

/** The stored position for a theme, or its default. Anything unparseable or out
 *  of range degrades to the default rather than blanking someone's map. */
export function mapDimFor(theme: DimTheme): number {
  const v = getPref(PREF_KEY[theme]);
  if (typeof v !== "number" || !Number.isFinite(v) || v < 0 || v > 1) return DIM_DEFAULTS[theme];
  return v;
}

/** Persist a position for a theme. Dusk and Night keep SEPARATE values on
 *  purpose: they describe different lighting situations, and one shared number
 *  would be wrong in whichever of the two it was not chosen for. */
export function setMapDim(theme: DimTheme, pos: number): void {
  setPref(PREF_KEY[theme], Math.max(0, Math.min(1, pos)));
}

/**
 * Apply a position to a map container.
 *
 * The class gate matters: a `filter` declaration promotes the canvas container
 * to its own compositing layer for as long as it is set, so an undimmed map
 * carries no filter at all rather than `brightness(1)`.
 */
export function applyMapDim(container: HTMLElement, pos: number): void {
  const b = dimToBrightness(pos);
  if (b >= 0.999) {
    container.classList.remove("mapdim");
    container.style.removeProperty("--map-dim");
    return;
  }
  container.style.setProperty("--map-dim", b.toFixed(4));
  container.classList.add("mapdim");
}

export interface MapDimmer {
  /** The control element — the caller appends it INSIDE the map container but
   *  outside the dimmed canvas container, which is what `.map > .mapdimctl`
   *  gives us for free. */
  root: HTMLElement;
  /** Follow a Day/Dusk/Night switch: re-read that theme's stored position,
   *  apply it, and show or hide the control. */
  setTheme(theme: "day" | "dusk" | "night"): void;
}

/**
 * Build the dimmer control for a map container.
 *
 * Placed in the map's own chrome rather than only in the settings panel: someone
 * whose eyes are adapting should not have to open a gear menu and read a list of
 * labels to turn the light down. A settings row alongside it is fine — this is
 * the one that has to be reachable in one gesture.
 */
export function createMapDimmer(container: HTMLElement): MapDimmer {
  const root = document.createElement("div");
  root.className = "mapdimctl";
  root.hidden = true;

  const label = document.createElement("span");
  label.className = "mdc-icon";
  label.textContent = "☼";
  label.setAttribute("aria-hidden", "true");

  const slider = document.createElement("input");
  slider.type = "range";
  slider.min = "0";
  slider.max = "100";
  slider.step = "1";
  slider.className = "mdc-range";
  slider.setAttribute("aria-label", "Map brightness");

  const out = document.createElement("span");
  out.className = "mdc-val";

  root.append(label, slider, out);

  let current: DimTheme | null = null;

  const render = (pos: number): void => {
    slider.value = String(Math.round(pos * 100));
    out.textContent = `${Math.round(pos * 100)}%`;
    applyMapDim(container, pos);
  };

  slider.addEventListener("input", () => {
    if (current === null) return;
    const pos = Number(slider.value) / 100;
    out.textContent = `${Math.round(pos * 100)}%`;
    applyMapDim(container, pos);
    setMapDim(current, pos);
  });

  return {
    root,
    setTheme(theme: "day" | "dusk" | "night"): void {
      if (theme === "day") {
        current = null;
        root.hidden = true;
        // Day is undimmed by definition — and leaving a stale filter on the
        // container after a Night→Day switch would darken a daylight map.
        applyMapDim(container, 1);
        return;
      }
      current = theme;
      root.hidden = false;
      render(mapDimFor(theme));
    },
  };
}
